import BaseAPI from './BaseAPI';

export const AGENT_NAMES = {
  CLINICAL_WORKFLOW_CHAT: 'clinical_workflow_chat',
} as const;

export type AgentName = (typeof AGENT_NAMES)[keyof typeof AGENT_NAMES] | string;

/**
 * Derive the canonical env key from the base_url returned by v4/login_check.
 * Falls back to apiEnv if baseUrl is not set.
 *
 * Examples:
 *   https://uat-temp-api.tigertext.me   → 'uat'
 *   https://temp-api.tigertext.me       → 'production'
 *   https://env7-temp-api.tigertext.xyz → 'env7'
 */
function resolveEnv(baseUrl: unknown, apiEnv: string): string {
  if (typeof baseUrl === 'string') {
    const prefix = new URL(baseUrl).hostname.split('.')[0].split('-')[0];
    if (prefix === 'api' || prefix === 'temp') return 'production';
    return prefix; // 'uat', 'env7', etc.
  }
  return apiEnv || 'production';
}

const AGENT_BASE_URLS: Record<string, string> = {
  production: 'https://agent.tigerconnect.com',
  uat: 'https://agent.uat.tigerconnect.com',
  env7: 'https://agent.env7.tigerconnect.com',
};

const IDP_TOKEN_ENDPOINTS: Record<string, string> = {
  production: 'https://idp.tigerconnect.com/connect/token',
  uat: 'https://idp.uat.tigerconnect.com/connect/token',
  env7: 'https://idp.env7.tigerconnect.com/connect/token',
};

const IDP_CLIENT_IDS: Record<string, string> = {
  production: 'web-exchange-prod',
  uat: 'web-exchange-uat',
  env7: 'web-exchange-dev',
};

export type SSEEvent =
  | { event: 'message_start'; data: { id: string; type: string } }
  | { event: 'token'; data: { text: string } }
  | { event: 'message'; data: { text: string } }
  | { event: 'tool_call'; data: { id: string; name: string; args: Record<string, unknown> } }
  | { event: 'tool_result'; data: { tool_call_id: string; name: string; content: string } }
  | { event: 'message_end'; data: { id: string; type: string } }
  | { event: 'done'; data: { messages: unknown[]; session_id: string } }
  | { event: 'error'; data: { error: string; error_type: string } };

export type InvokeAgentOptions = {
  agentName?: AgentName;
  messages: Array<{ role: string; content: string }>;
  stream?: boolean;
  sessionId?: string;
  organizationId?: string;
  signal?: AbortSignal;
};

type CachedToken = { token: string; expiresAt: number };

export default class AgentCoreAPI extends BaseAPI {
  // Cache key: `${organizationId}:${auth.key}` — credential-aware so a key
  // rotation or re-auth with different credentials naturally produces a cache miss.
  private tokenCache = new Map<string, CachedToken>();
  // The agent backend rejects tokens expiring within 1 minute; use 65 s so the
  // cache evicts them before the server does.
  private static readonly TOKEN_EXPIRY_BUFFER_MS = 65_000;

  constructor(...args: ConstructorParameters<typeof BaseAPI>) {
    super(...args);
    // Clear the token cache whenever the client signs out so a subsequent
    // sign-in (potentially with a different user) cannot reuse stale tokens.
    // The listener is never removed because AgentCoreAPI and its host share
    // the same lifetime — removing it in dispose() would break post-reset
    // sessions since Client.reset() disposes then remounts without re-registering.
    this.host.on('signedOut', () => this.clearTokenCache());
  }

  dispose(): void {
    // Clear cached OAuth tokens on dispose/reset so stale tokens from a
    // previous session cannot be reused after Client.reset().
    this.tokenCache.clear();
  }

  /**
   * Evict cached OAuth tokens.
   * With an organizationId: drops all entries for that org (across any credential).
   * Without an argument: clears everything (used on sign-out).
   */
  clearTokenCache(organizationId?: string): void {
    if (organizationId !== undefined) {
      const prefix = `${organizationId}:`;
      Array.from(this.tokenCache.keys()).forEach((key) => {
        if (key.startsWith(prefix)) {
          this.tokenCache.delete(key);
        }
      });
    } else {
      this.tokenCache.clear();
    }
  }

  /**
   * Get the agent invocation URL for the current environment.
   * Format: https://agent.{env}.tigerconnect.com/v1/agents/{agent_name}/invocations
   */
  getInvocationUrl(agentName: AgentName = AGENT_NAMES.CLINICAL_WORKFLOW_CHAT): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    const baseUrl = AGENT_BASE_URLS[env] || `https://agent.${env}.tigerconnect.com`;
    return `${baseUrl}/v1/agents/${agentName}/invocations`;
  }

  /**
   * Get the IDP token endpoint for the current environment.
   */
  getIdpTokenEndpoint(): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    return IDP_TOKEN_ENDPOINTS[env] || `https://idp.${env}.tigerconnect.com/connect/token`;
  }

  /**
   * Get the IDP client ID for the current environment.
   */
  getIdpClientId(): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    return IDP_CLIENT_IDS[env] || 'web-exchange-dev';
  }

  /**
   * Exchange API key/secret for an OAuth JWT via the IDP.
   *
   * Tokens are cached per `organizationId:auth.key` and reused until 30 seconds
   * before expiry, avoiding a /connect/token round-trip on every invocation.
   * Including auth.key in the key ensures a credential rotation produces a miss.
   */
  async getOAuthToken(organizationId: string): Promise<string> {
    const auth = this.httpClient.getAuth();
    if (!auth) {
      throw new Error('Not authenticated — cannot exchange for OAuth token');
    }

    const cacheKey = `${organizationId}:${auth.key}`;
    const now = Date.now();
    const cached = this.tokenCache.get(cacheKey);
    if (cached && cached.expiresAt - now > AgentCoreAPI.TOKEN_EXPIRY_BUFFER_MS) {
      return cached.token;
    }

    const body = new URLSearchParams({
      grant_type: 'api_key',
      api_key: auth.key,
      api_secret: auth.secret,
      client_id: this.getIdpClientId(),
      org_id: organizationId,
      scope: 'agent:invoke openid profile email api.internal.basic',
    });

    const response = await fetch(this.getIdpTokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error(`OAuth token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    const token: string = data.access_token;
    // Cache only when the JWT exp claim is readable — if parsing fails, always fetch fresh.
    const expiresAt = AgentCoreAPI.parseJwtExpiry(token);
    if (expiresAt !== null) {
      this.tokenCache.set(cacheKey, { token, expiresAt });
    }
    return token;
  }

  /**
   * Extract the `exp` claim (ms) from a JWT payload without verifying the signature.
   * Returns null if the token is malformed or has no `exp` claim.
   */
  private static parseJwtExpiry(token: string): number | null {
    try {
      const segment = token.split('.')[1];
      if (!segment) return null;
      // JWT uses base64url; normalize to base64 then decode
      const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
      const json =
        typeof Buffer !== 'undefined'
          ? Buffer.from(base64, 'base64').toString('utf-8')
          : atob(base64);
      const { exp } = JSON.parse(json);
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /**
   * Invoke an AgentCore agent and stream SSE events.
   *
   * @param options - Invocation options
   * @param onEvent - Callback for each SSE event
   */
  async invoke(options: InvokeAgentOptions, onEvent: (event: SSEEvent) => void): Promise<void> {
    const { agentName, messages, stream = true, sessionId, organizationId, signal } = options;

    if (!organizationId) {
      throw new Error('organizationId is required for agent invocation');
    }

    const doFetch = async (tok: string) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tok}`,
      };
      if (sessionId) {
        headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = sessionId;
      }
      return fetch(this.getInvocationUrl(agentName), {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages, stream, session_id: sessionId }),
        signal,
      });
    };

    let token = await this.getOAuthToken(organizationId);
    let response = await doFetch(token);

    // On 401 the server may have rejected a near-expiry token the cache let through.
    // Evict the stale entry and retry once with a fresh token.
    if (response.status === 401) {
      this.clearTokenCache(organizationId);
      token = await this.getOAuthToken(organizationId);
      response = await doFetch(token);
    }

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Agent invocation failed (${response.status}): ${errorBody}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          let eventData: SSEEvent;
          try {
            eventData = JSON.parse(trimmed.slice(6)) as SSEEvent;
          } catch {
            // skip malformed SSE lines
            continue;
          }
          onEvent(eventData);
        }
      }

      // Flush any remaining data in the buffer if the stream ended without a trailing newline
      const remaining = buffer.trim();
      if (remaining && remaining.startsWith('data: ')) {
        let eventData: SSEEvent;
        try {
          eventData = JSON.parse(remaining.slice(6)) as SSEEvent;
        } catch {
          // skip malformed SSE lines
          return;
        }
        onEvent(eventData);
      }
    } finally {
      reader.releaseLock();
    }
  }
}
